import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import {
  FaLock, FaSchool, FaUser, FaPhone, FaEnvelope,
  FaMapMarkerAlt, FaToggleOn, FaTimes, FaSave,
} from 'react-icons/fa';
import { schoolAPI, talukaAPI, districtAPI, regionAPI, userAPI } from '../../../../utils/api';
import '../../../../styles/institutional-form.css';
import '../css/schoolCreate.css';

const TYPES  = ['Boys', 'Girls', 'Mixed'];
const LEVELS = ['Primary', 'Middle', 'High', 'Higher Secondary'];

function SchoolCreate({ school = null, onClose, onSubmit }) {
  const isEdit = !!school;

  const [semis, setSemis]   = useState('');
  const [emis, setEmis]     = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [schoolType, setSchoolType] = useState('');
  const [level, setLevel]   = useState('');
  const [hmName, setHmName] = useState('');
  const [toTeoUserId, setToTeoUserId] = useState('');
  const [headMasterUserId, setHeadMasterUserId] = useState('');
  const [phone, setPhone]   = useState('');
  const [email, setEmail]   = useState('');
  const [address, setAddress] = useState('');
  const [location, setLocation] = useState('');
  const [talukaId, setTalukaId] = useState('');

  const [regions, setRegions]     = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas]     = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [selRegion, setSelRegion] = useState('');
  const [selDistrict, setSelDistrict] = useState('');

  const [errors, setErrors]       = useState({});
  const [busy, setBusy]           = useState(false);
  const [codeLoading, setCodeLoading] = useState(!isEdit);

  // Load geography lookups
  useEffect(() => {
    Promise.all([
      regionAPI.getAll(),
      districtAPI.getAll(),
      talukaAPI.getAll(),
      userAPI.getUsers({ page: 0, size: 500, status: 'ACTIVE' }),
    ])
      .then(([r, d, t, users]) => {
        setRegions(r.data || []);
        setDistricts(d.data || []);
        setTalukas(t.data || []);
        setStaffUsers(users.data?.content || users.data || []);
      })
      .catch(() => {});
  }, []);

  // Prefill when editing
  useEffect(() => {
    if (isEdit && talukas.length) {
      setSemis(school.semis_code || '');
      setEmis(school.emis_code || '');
      setSchoolName(school.school_name || school.name || '');
      setSchoolType(school.school_type || '');
      setLevel(school.level || '');
      setHmName(school.headmaster_name || '');
      setToTeoUserId(school.to_teo_user_id ? String(school.to_teo_user_id) : '');
      setHeadMasterUserId(school.head_master_user_id ? String(school.head_master_user_id) : '');
      setPhone(school.phone || '');
      setEmail(school.email || '');
      setAddress(school.address || '');
      setLocation(
        school.location_lat != null && school.location_lng != null
          ? `${school.location_lat}, ${school.location_lng}`
          : ''
      );
      const tid = String(school.taluka_id || '');
      setTalukaId(tid);
      // cascade-select region & district
      const tal = talukas.find(t => String(t.id) === tid);
      if (tal) {
        const dist = districts.find(d => String(d.id) === String(tal.district_id));
        if (dist) {
          setSelDistrict(String(dist.id));
          const reg = regions.find(r => String(r.id) === String(dist.region_id));
          if (reg) setSelRegion(String(reg.id));
        }
      }
    }
  }, [isEdit, school, talukas, districts, regions]);

  // Auto-generate SEMIS code for create
  useEffect(() => {
    if (!isEdit) {
      setCodeLoading(true);
      schoolAPI.nextCode()
        .then(r => setSemis(r.data.code))
        .catch(() => setSemis('SCH-?????'))
        .finally(() => setCodeLoading(false));
    }
  }, [isEdit]);

  const filteredDistricts = districts.filter(d => String(d.region_id) === selRegion);
  const filteredTalukas   = talukas.filter(t => String(t.district_id) === selDistrict);
  const staffLabel = u => [u.first_name || u.firstName, u.last_name || u.lastName].filter(Boolean).join(' ') || u.username || u.email;

  const clearErr = key => setErrors(p => ({ ...p, [key]: '' }));

  const validate = () => {
    const e = {};
    if (!schoolName.trim())  e.schoolName = 'School name is required';
    if (!schoolType)         e.schoolType = 'Select a school type';
    if (!level)              e.level      = 'Select a level';
    if (!talukaId)           e.talukaId   = 'Select a taluka';
    if (!hmName.trim())      e.hmName     = 'Headmaster name is required';
    if (phone && !/^(\+92|0)?[0-9]{10,11}$/.test(phone.replace(/[\s\-()]/g, '')))
      e.phone = 'Enter a valid Pakistani phone number';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = 'Enter a valid email address';
    if (location && !/^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(location))
      e.location = 'Format: latitude, longitude (e.g. 24.8607, 67.0011)';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async ev => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        semis_code:      semis || null,
        emis_code:       emis || null,
        school_name:     schoolName.trim(),
        school_type:     schoolType || null,
        level:           level || null,
        taluka_id:       talukaId ? Number(talukaId) : null,
        headmaster_name: hmName.trim() || null,
        to_teo_user_id:  toTeoUserId ? Number(toTeoUserId) : null,
        head_master_user_id: headMasterUserId ? Number(headMasterUserId) : null,
        phone:           phone || null,
        email:           email || null,
        address:         address || null,
        ...(location && /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(location)
          ? (() => { const [lat, lng] = location.split(',').map(s => s.trim()); return { location_lat: Number(lat), location_lng: Number(lng) }; })()
          : {}),
      };
      if (isEdit) await schoolAPI.update(school.id, payload);
      else        await schoolAPI.create(payload);
      Swal.fire('Success', isEdit ? 'School updated.' : 'School created.', 'success');
      onSubmit?.();
      onClose?.();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.error || err.message || 'Failed', 'error');
    } finally { setBusy(false); }
  };

  return (
    <form className="ifrm" onSubmit={handleSubmit}>

      {/* ── System Identifier ─────────────────────────────────────── */}
      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaLock style={{color:'#10ac84'}} /> System Identifier</div>
        <div className="ifrm-grid">
          <div className="ifrm-field">
            <label className="ifrm-label">
              SEMIS Code <span className="ifrm-badge-auto">Auto-generated</span>
            </label>
            {codeLoading
              ? <p className="ifrm-code-loading">Generating code…</p>
              : (
                <div className="ifrm-locked-wrap">
                  <FaLock className="ifrm-lock-icon" />
                  <input className="ifrm-input" value={semis} disabled />
                </div>
              )
            }
          </div>
          <div className="ifrm-field">
            <label className="ifrm-label">EMIS Code <span style={{fontSize:'0.74rem',color:'#94a3b8',fontWeight:400}}>(optional)</span></label>
            <input
              className="ifrm-input"
              placeholder="e.g. EM0042"
              value={emis}
              onChange={e => setEmis(e.target.value)}
              disabled={busy}
              maxLength={8}
            />
          </div>
        </div>
      </div>

      {/* ── Basic Info ────────────────────────────────────────────── */}
      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaSchool style={{color:'#0f6fad'}} /> Basic Information</div>

        <div className="ifrm-field">
          <label className="ifrm-label">School Name <span className="ifrm-required">*</span></label>
          <input
            className={`ifrm-input${errors.schoolName ? ' error' : ''}`}
            placeholder="Enter school name"
            value={schoolName}
            onChange={e => { setSchoolName(e.target.value); clearErr('schoolName'); }}
            disabled={busy}
          />
          {errors.schoolName && <p className="ifrm-error">{errors.schoolName}</p>}
        </div>

        <div className="ifrm-grid">
          <div className="ifrm-field">
            <label className="ifrm-label">School Type <span className="ifrm-required">*</span></label>
            <select
              className={`ifrm-select${errors.schoolType ? ' error' : ''}`}
              value={schoolType}
              onChange={e => { setSchoolType(e.target.value); clearErr('schoolType'); }}
              disabled={busy}
            >
              <option value="">Select type</option>
              {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {errors.schoolType && <p className="ifrm-error">{errors.schoolType}</p>}
          </div>
          <div className="ifrm-field">
            <label className="ifrm-label">Level <span className="ifrm-required">*</span></label>
            <select
              className={`ifrm-select${errors.level ? ' error' : ''}`}
              value={level}
              onChange={e => { setLevel(e.target.value); clearErr('level'); }}
              disabled={busy}
            >
              <option value="">Select level</option>
              {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            {errors.level && <p className="ifrm-error">{errors.level}</p>}
          </div>
        </div>
      </div>

      {/* ── Location Cascade ──────────────────────────────────────── */}
      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaMapMarkerAlt style={{color:'#f59e0b'}} /> Administrative Location</div>

        <div className="ifrm-grid">
          <div className="ifrm-field">
            <label className="ifrm-label">Region</label>
            <select
              className="ifrm-select"
              value={selRegion}
              onChange={e => { setSelRegion(e.target.value); setSelDistrict(''); setTalukaId(''); }}
              disabled={busy}
            >
              <option value="">Select region</option>
              {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="ifrm-field">
            <label className="ifrm-label">District</label>
            <select
              className="ifrm-select"
              value={selDistrict}
              onChange={e => { setSelDistrict(e.target.value); setTalukaId(''); }}
              disabled={busy || !selRegion}
            >
              <option value="">Select district</option>
              {filteredDistricts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>

        <div className="ifrm-field">
          <label className="ifrm-label">Taluka <span className="ifrm-required">*</span></label>
          <select
            className={`ifrm-select${errors.talukaId ? ' error' : ''}`}
            value={talukaId}
            onChange={e => { setTalukaId(e.target.value); clearErr('talukaId'); }}
            disabled={busy || !selDistrict}
          >
            <option value="">Select taluka</option>
            {filteredTalukas.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {errors.talukaId && <p className="ifrm-error">{errors.talukaId}</p>}
        </div>
      </div>

      {/* ── Admin Contact ─────────────────────────────────────────── */}
      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaUser style={{color:'#8b5cf6'}} /> Headmaster &amp; Contact</div>

        <div className="ifrm-field">
          <label className="ifrm-label"><FaUser style={{color:'#64748b'}} /> Headmaster Name <span className="ifrm-required">*</span></label>
          <input
            className={`ifrm-input${errors.hmName ? ' error' : ''}`}
            placeholder="Full name"
            value={hmName}
            onChange={e => { setHmName(e.target.value); clearErr('hmName'); }}
            disabled={busy}
          />
          {errors.hmName && <p className="ifrm-error">{errors.hmName}</p>}
        </div>

        <div className="ifrm-grid">
          <div className="ifrm-field">
            <label className="ifrm-label"><FaUser style={{color:'#64748b'}} /> Assigned TO/TEO</label>
            <select
              className="ifrm-select"
              value={toTeoUserId}
              onChange={e => setToTeoUserId(e.target.value)}
              disabled={busy}
            >
              <option value="">Not assigned</option>
              {staffUsers.map(u => <option key={u.id} value={u.id}>{staffLabel(u)}</option>)}
            </select>
          </div>
          <div className="ifrm-field">
            <label className="ifrm-label"><FaUser style={{color:'#64748b'}} /> Head Master Portal User</label>
            <select
              className="ifrm-select"
              value={headMasterUserId}
              onChange={e => setHeadMasterUserId(e.target.value)}
              disabled={busy}
            >
              <option value="">Not assigned</option>
              {staffUsers.map(u => <option key={u.id} value={u.id}>{staffLabel(u)}</option>)}
            </select>
          </div>
        </div>

        <div className="ifrm-grid">
          <div className="ifrm-field">
            <label className="ifrm-label"><FaPhone style={{color:'#64748b'}} /> Phone</label>
            <input
              className={`ifrm-input${errors.phone ? ' error' : ''}`}
              placeholder="03XX-XXXXXXX"
              value={phone}
              onChange={e => { setPhone(e.target.value); clearErr('phone'); }}
              disabled={busy}
            />
            {errors.phone && <p className="ifrm-error">{errors.phone}</p>}
          </div>
          <div className="ifrm-field">
            <label className="ifrm-label"><FaEnvelope style={{color:'#64748b'}} /> Email</label>
            <input
              type="email"
              className={`ifrm-input${errors.email ? ' error' : ''}`}
              placeholder="school@example.com"
              value={email}
              onChange={e => { setEmail(e.target.value); clearErr('email'); }}
              disabled={busy}
            />
            {errors.email && <p className="ifrm-error">{errors.email}</p>}
          </div>
        </div>
      </div>

      {/* ── Location Info ─────────────────────────────────────────── */}
      <div className="ifrm-section">
        <div className="ifrm-section-title"><FaMapMarkerAlt style={{color:'#ef4444'}} /> Location Details</div>

        <div className="ifrm-field">
          <label className="ifrm-label"><FaToggleOn style={{color:'#64748b'}} /> Address</label>
          <textarea
            className="ifrm-input"
            placeholder="Street, area, city"
            rows={2}
            style={{resize:'vertical'}}
            value={address}
            onChange={e => setAddress(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="ifrm-field">
          <label className="ifrm-label"><FaMapMarkerAlt style={{color:'#64748b'}} /> GPS Coordinates</label>
          <input
            className={`ifrm-input${errors.location ? ' error' : ''}`}
            placeholder="24.8607, 67.0011"
            value={location}
            onChange={e => { setLocation(e.target.value); clearErr('location'); }}
            disabled={busy}
          />
          {errors.location
            ? <p className="ifrm-error">{errors.location}</p>
            : <p className="ifrm-hint">Format: latitude, longitude</p>
          }
        </div>
      </div>

      <div className="ifrm-actions">
        <button type="button" className="ifrm-btn ifrm-btn-cancel" onClick={onClose} disabled={busy}>
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="ifrm-btn ifrm-btn-submit" disabled={busy || codeLoading}>
          {busy ? <><span className="ifrm-spinner" /> Saving…</> : <><FaSave /> {isEdit ? 'Update School' : 'Create School'}</>}
        </button>
      </div>
    </form>
  );
}

SchoolCreate.propTypes = { school: PropTypes.object, onClose: PropTypes.func, onSubmit: PropTypes.func };
export default SchoolCreate;
